import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { Voter, JoinRoomRequest, SubmitVoteRequest, EstimateType } from '../types/api'
import { apiClient } from '../services/apiClient'
import { useRoomStore } from './room'

export const useVoterStore = defineStore('voter', () => {
  // State
  const currentVoter = ref<Voter | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Getters
  // Returns the stored voter name for form prepopulation (Requirements 2.8, 3.10, 18.2)
  const storedVoterName = computed(() => {
    // First check if we have a current voter with a name
    if (currentVoter.value?.voterName) {
      return currentVoter.value.voterName
    }
    // Fall back to persistent User_Name_Preference from localStorage
    try {
      return localStorage.getItem('agile-tool-chest-user-name-preference')
    } catch (error) {
      console.error('Failed to read user name preference:', error)
      return null
    }
  })

  // Initialize from localStorage on store creation
  const initializeFromStorage = () => {
    try {
      const storedVoter = localStorage.getItem('agile-tool-chest-current-voter')
      if (storedVoter) {
        const voter = JSON.parse(storedVoter)
        console.log('Restored voter from localStorage:', voter)
        currentVoter.value = voter
      }
    } catch (error) {
      console.error('Failed to restore voter from localStorage:', error)
      localStorage.removeItem('agile-tool-chest-current-voter')
    }
  }

  // Save to localStorage whenever currentVoter changes
  const saveToStorage = (voter: Voter | null) => {
    try {
      if (voter) {
        localStorage.setItem('agile-tool-chest-current-voter', JSON.stringify(voter))
        console.log('Saved voter to localStorage:', voter)
      } else {
        localStorage.removeItem('agile-tool-chest-current-voter')
        console.log('Removed voter from localStorage')
      }
    } catch (error) {
      console.error('Failed to save voter to localStorage:', error)
    }
  }

  // Initialize from storage when store is created
  initializeFromStorage()

  // Actions
  async function joinRoom(roomKey: string, voterName: string): Promise<Voter> {
    try {
      loading.value = true
      error.value = null
      
      const request: JoinRoomRequest = { userName: voterName }
      const voter = await apiClient.joinRoom(roomKey, request)
      
      currentVoter.value = voter
      saveToStorage(voter)
      
      // Save user name preference for future form prepopulation (Requirement 18.1, 18.4)
      saveUserNamePreference(voterName)
      
      return voter
    } catch (err: any) {
      error.value = err.message || 'Failed to join room'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function submitEstimationVote(
    roomKey: string, 
    voterKey: string, 
    value: number | string, 
    round: number,
    estimateType?: EstimateType
  ): Promise<void> {
    try {
      loading.value = true
      error.value = null
      
      const request: SubmitVoteRequest = { 
        vote: value, 
        votingRound: round,
        ...(estimateType && { estimateType })
      }
      await apiClient.submitEstimationVote(roomKey, voterKey, request)
      
      // Update local voter state optimistically
      if (currentVoter.value && currentVoter.value.voterKey === voterKey) {
        // todo: deeply nested, looks ugly
        if (estimateType) {
          // Dual estimate mode: update the specific estimate type slot
          if (!currentVoter.value.estimationPokerDualVotes) {
            currentVoter.value.estimationPokerDualVotes = {} as Record<EstimateType, { value: number | string; votingRound: number }>
          }
          currentVoter.value.estimationPokerDualVotes[estimateType] = {
            value,
            votingRound: round
          }
        } else {
          // Single estimate mode: update the traditional field
          currentVoter.value.estimationPokerVote = {
            value,
            votingRound: round
          }
        }
        saveToStorage(currentVoter.value)
      }
      
      // Also update the room store's voters array for immediate UI feedback
      const roomStore = useRoomStore()
      if (estimateType) {
        roomStore.updateVoterDualVote(voterKey, estimateType, value, round)
      } else {
        roomStore.updateVoterVote(voterKey, 'estimation', value, round)
      }
    } catch (err: any) {
      console.error('Error in submitEstimationVote:', err)
      error.value = err.message || 'Failed to submit estimation vote'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function submitConfidenceVote(
    roomKey: string, 
    voterKey: string, 
    value: number, 
    round: number
  ): Promise<void> {
    try {
      loading.value = true
      error.value = null
      
      const request: SubmitVoteRequest = { vote: value, votingRound: round }
      await apiClient.submitConfidenceVote(roomKey, voterKey, request)
      
      // Update local voter state optimistically
      if (currentVoter.value && currentVoter.value.voterKey === voterKey) {
        currentVoter.value.confidenceVote = {
          value,
          votingRound: round
        }
      }
      
      // Also update the room store's voters array for immediate UI feedback
      const roomStore = useRoomStore()
      roomStore.updateVoterVote(voterKey, 'confidence', value, round)
    } catch (err: any) {
      error.value = err.message || 'Failed to submit confidence vote'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function removeVoter(roomKey: string, voterKey: string): Promise<void> {
    try {
      loading.value = true
      error.value = null
      
      await apiClient.removeVoter(roomKey, voterKey)
      
      // If we removed the current voter, clear the state
      if (currentVoter.value && currentVoter.value.voterKey === voterKey) {
        currentVoter.value = null
        saveToStorage(null)
      }
    } catch (err: any) {
      error.value = err.message || 'Failed to remove voter'
      throw err
    } finally {
      loading.value = false
    }
  }

  function clearError(): void {
    error.value = null
  }

  function resetState(): void {
    currentVoter.value = null
    loading.value = false
    error.value = null
    saveToStorage(null)
  }

  // User name preference management (Requirement 18: Persistent User Name Preference)
  
  /**
   * Permanently stores the user's name preference in localStorage.
   * This preference survives browser sessions and is used for form prepopulation.
   */
  function saveUserNamePreference(name: string): void {
    try {
      localStorage.setItem('agile-tool-chest-user-name-preference', name)
      console.log('Saved user name preference:', name)
    } catch (error) {
      console.error('Failed to save user name preference:', error)
    }
  }

  return {
    // State
    currentVoter,
    loading,
    error,
    // Getters
    storedVoterName,
    // Actions
    joinRoom,
    submitEstimationVote,
    submitConfidenceVote,
    removeVoter,
    clearError,
    resetState,
    saveUserNamePreference,
  }
})